import { BRAND_PERSON_NAME, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { useEffect, useRef, useState } from "react";
import { getDatafastAttributionPayload } from "@/lib/datafast";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useSiteSettingsVariant } from "@/hooks/useSiteSettingsVariant";
import { useInternationalHomeVipCheckoutPricing } from "@/hooks/useInternationalHomeVipCheckoutPricing";

const MEMBRESIA_EXPIRED_KEY = "membresia-exclusiva-expired";

// **Política**: Un solo tier ("Membresía Exclusiva").
// Precio + Stripe Price/promo = admin VIP (country-templates / vipPricingDefault),
// misma fuente que DualCTA. Fallback server: membership_products + ANA70.
const PRODUCT_NAME = "Membresía Exclusiva";

/**
 * Página `/membresia-telegram` — oferta única de **Membresía Exclusiva**.
 * Countdown 60s; cuando expira la oferta queda deshabilitada (sin segundo tier).
 *
 * Post-pago: Stripe redirige a `MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO`
 * (`/portal/completar-perfil`) con `session_id` para que el visitante complete
 * el perfil en Clerk antes de pasar a `pending-approval`.
 */
const getInitialState = (): {
  countdown: number;
  isButtonEnabled: boolean;
} => {
  if (typeof window === "undefined") {
    return { countdown: 60, isButtonEnabled: true };
  }
  const expired = localStorage.getItem(MEMBRESIA_EXPIRED_KEY) === "1";
  if (expired) return { countdown: 0, isButtonEnabled: false };
  return { countdown: 60, isButtonEnabled: true };
};

const MembresiaTelegram = () => {
  const initial = getInitialState();
  const [countdown, setCountdown] = useState(() => initial.countdown);
  const [isButtonEnabled, setIsButtonEnabled] = useState(() => initial.isButtonEnabled);
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const countdownActiveRef = useRef(initial.isButtonEnabled);

  const { country } = useTemplateSelection();
  const siteSettingsVariant = useSiteSettingsVariant();
  const { settings: siteSettings } = useSiteSettings(siteSettingsVariant);
  const { pricing: adminVipPricing, priceDisplay } =
    useInternationalHomeVipCheckoutPricing({
      country,
      vipPricingByCountry: siteSettings.internationalHomeVipPricingByCountry,
      vipPricingDefault: siteSettings.internationalHomeVipPricingDefault,
      language: "es",
    });

  // Countdown: tras 60s la oferta única queda deshabilitada permanentemente.
  useEffect(() => {
    if (!countdownActiveRef.current) return;
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    countdownActiveRef.current = false;
    localStorage.setItem(MEMBRESIA_EXPIRED_KEY, "1");
    setIsButtonEnabled(false);
  }, [countdown]);

  const handleSubscribe = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setCheckoutError(null);
    try {
      const origin = window.location.origin;
      // Post-pago: Clerk en `/portal/completar-perfil`. Stripe agrega `session_id`.
      const successUrl =
        `${origin}${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}` +
        `?plan=exclusiva&source=membresia_exclusiva`;
      const cancelUrl = `${origin}/membresia-telegram`;

      const amountCents =
        adminVipPricing.amountCents > 0
          ? adminVipPricing.amountCents
          : adminVipPricing.fullPriceCents || 2999;

      const res = await fetch("/api/create-subscription-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...getDatafastAttributionPayload(),
          productName: PRODUCT_NAME,
          successUrl,
          cancelUrl,
          intervalCount: 1,
          currency: "usd",
          amount: amountCents,
          country: country || undefined,
          ...(adminVipPricing.stripePriceId
            ? { priceId: adminVipPricing.stripePriceId }
            : {}),
          ...(adminVipPricing.promotionCodeId
            ? { promotionCodeId: adminVipPricing.promotionCodeId }
            : {}),
          metadata: {
            source: BRAND_SOURCE_KEY,
            flow: "membresia_exclusiva",
            plan_tier: "vip",
            product_name: PRODUCT_NAME,
            product_type: "exclusive",
            price_source: adminVipPricing.priceSource,
            ...(adminVipPricing.stripePriceId
              ? { stripe_price_id: adminVipPricing.stripePriceId }
              : {}),
          },
        }),
      });
      const data = (await res.json()) as { url?: string; error?: string; code?: string };
      if (!res.ok || !data.url) {
        const detail =
          typeof data.error === "string" && data.error
            ? data.code
              ? `${data.error} (${data.code})`
              : data.error
            : `No se pudo iniciar el checkout (HTTP ${res.status})`;
        throw new Error(detail);
      }
      window.location.href = data.url;
    } catch (err) {
      console.error("[MembresiaTelegram] checkout error:", err);
      setCheckoutError(
        err instanceof Error ? err.message : "Error iniciando el checkout",
      );
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="text-center space-y-8 max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src={HEADER_PROFILE_IMAGE_URL}
            alt={BRAND_PERSON_NAME}
            className="w-32 h-32 rounded-full border-4 border-pink-500/50 shadow-2xl shadow-pink-500/20"
          />
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl font-bold tracking-tight">Membresía Exclusiva</h1>
          <p className="text-white/60 text-sm leading-relaxed">
            Acceso completo. Un solo plan. Oferta por tiempo limitado.
          </p>
          <div className="pt-2">
            {priceDisplay.originalPriceLine ? (
              <p className="text-sm text-white/40 line-through">
                {priceDisplay.originalPriceLine}
              </p>
            ) : null}
            <p className="text-2xl font-bold text-amber-400/95">
              {priceDisplay.priceLine}
            </p>
          </div>
        </div>

        {countdown > 0 && isButtonEnabled ? (
          <p className="text-sm text-pink-400/90 tabular-nums">
            Oferta expira en {countdown}s
          </p>
        ) : (
          <p className="text-sm text-white/40">Oferta no disponible</p>
        )}

        {checkoutError ? (
          <p className="text-sm text-red-400" role="alert">
            {checkoutError}
          </p>
        ) : null}

        <button
          type="button"
          disabled={!isButtonEnabled || isProcessing}
          onClick={() => void handleSubscribe()}
          className="w-full rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-pink-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          {isProcessing ? "Abriendo checkout…" : "Suscribirme ahora"}
        </button>
      </div>
    </div>
  );
};

export default MembresiaTelegram;
